import React, { useState } from 'react';
import { colors } from '../styles/colors';

interface RepublishModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  opportunityName: string;
}

const RepublishModal: React.FC<RepublishModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  opportunityName
}) => {
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    try {
      setLoading(true);
      await onConfirm();
      onClose();
    } catch (error) {
      // Error is handled by the parent component
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <div style={styles.overlay} onClick={handleClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div style={styles.iconContainer}>
            <span style={styles.icon}>🔄</span>
          </div>
          <h2 style={styles.title}>Republicar Vaga</h2>
        </div>
        
        <div style={styles.content}>
          <div style={styles.infoBox}>
            <strong>Esta ação irá republicar a vaga</strong>
          </div>
          
          <p style={styles.message}>
            Tem certeza que deseja <strong>republicar</strong> a vaga:
          </p>
          <div style={styles.opportunityName}>
            "{opportunityName}"
          </div>
          
          <div style={styles.consequencesList}>
            <p style={styles.consequencesTitle}>O que acontecerá:</p>
            <ul style={styles.consequences}>
              <li>A data de publicação será <strong>atualizada</strong> para agora</li>
              <li>O contador de republicações será <strong>incrementado</strong></li>
              <li>A vaga aparecerá como <strong>nova</strong> nas listagens</li>
              <li>A data de publicação original será <strong>preservada</strong></li>
            </ul>
          </div>
          
          <p style={styles.finalInfo}>
            Esta ação irá <strong>republicar</strong> a vaga.
          </p>
          
          {loading && (
            <div style={styles.loadingContainer}>
              <div style={styles.spinner}></div>
              <span style={styles.loadingText}>Republicando vaga...</span>
            </div>
          )}
        </div>
        
        <div style={styles.actions}>
          <button 
            onClick={handleClose} 
            style={{
              ...styles.cancelBtn,
              ...(loading ? styles.disabledBtn : {})
            }}
            disabled={loading}
          >
            Cancelar
          </button>
          <button 
            onClick={handleConfirm} 
            style={{
              ...styles.confirmBtn,
              ...(loading ? styles.disabledBtn : {})
            }}
            disabled={loading}
          >
            {loading ? 'Republicando...' : 'Republicar Vaga'}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1001,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    width: '95%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
  },
  header: {
    padding: '2rem 2rem 1rem 2rem',
    textAlign: 'center',
    backgroundColor: '#F0F9FF',
  },
  iconContainer: {
    marginBottom: '1rem',
  },
  icon: {
    fontSize: '3rem',
    display: 'block',
  },
  title: {
    margin: 0,
    color: colors.primary,
    fontSize: '1.5rem',
    fontWeight: 'bold',
  },
  content: {
    padding: '1.5rem 2rem',
  },
  infoBox: {
    padding: '1rem',
    backgroundColor: '#F0F9FF',
    border: `2px solid ${colors.primary}`,
    borderRadius: '8px',
    color: colors.primary,
    textAlign: 'center',
    fontSize: '1rem',
    fontWeight: 'bold',
    marginBottom: '1.5rem',
  },
  message: {
    margin: '0 0 1rem 0',
    color: colors.text,
    fontSize: '1rem',
    lineHeight: '1.5',
    textAlign: 'center',
  },
  opportunityName: {
    padding: '1rem',
    backgroundColor: colors.background,
    borderRadius: '8px',
    border: `2px solid ${colors.primary}`,
    color: colors.primary,
    fontWeight: 'bold',
    fontSize: '1.1rem',
    margin: '1rem 0 1.5rem 0',
    textAlign: 'center',
  },
  consequencesList: {
    marginBottom: '1.5rem',
  },
  consequencesTitle: {
    margin: '0 0 0.5rem 0',
    color: colors.text,
    fontSize: '1rem',
    fontWeight: 'bold',
  },
  consequences: {
    margin: '0',
    paddingLeft: '1.5rem',
    color: colors.textLight,
    fontSize: '0.9rem',
    lineHeight: '1.6',
  },
  finalInfo: {
    margin: '0',
    color: colors.primary,
    fontSize: '0.95rem',
    fontWeight: 'bold',
    textAlign: 'center',
    padding: '1rem',
    backgroundColor: '#F0F9FF',
    borderRadius: '8px',
  },
  actions: {
    display: 'flex',
    gap: '1rem',
    padding: '1.5rem 2rem 2rem 2rem',
    justifyContent: 'center',
  },
  cancelBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.white,
    color: colors.textLight,
    border: `2px solid ${colors.border}`,
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontFamily: 'inherit',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  confirmBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: `2px solid ${colors.primary}`,
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontFamily: 'inherit',
    fontWeight: 'bold',
    transition: 'all 0.2s ease',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    marginTop: '1rem',
    padding: '1rem',
    backgroundColor: colors.background,
    borderRadius: '4px',
  },
  spinner: {
    width: '20px',
    height: '20px',
    border: `2px solid ${colors.border}`,
    borderTop: `2px solid ${colors.primary}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    fontSize: '0.9rem',
    color: colors.textLight,
  },
  disabledBtn: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
};

export default RepublishModal;